import React, {useEffect, useState} from 'react';
import {Alert, Button, Form, Input, Skeleton, Space, Table, Tag} from 'antd';
import AdminActionsDropdown from '../../../CommonComponents/AdminActionsDropdown/AdminActionsDropdown';
import EditableCell from '../../../CommonComponents/EditableCell';
import {
  formValueChangeHandler,
  scrollAndFocusFormErrorField,
} from '../../../../Util/commonUtility';
import useLocalization from '../../../../Localization';

const SponsorDetailCartForm = ({
  localUserData,
  eventSponsorData,
  alertInformation,
  onFinish,
  onlyForView,
}) => {
  const {ApplicationString} = useLocalization();
  const isEditing = (record) => record.id === editingKey;
  const [dataSource, setDataSource] = useState([]);
  const [sponsorCreatedSuccessFully, setSponsorCreatedSuccessFully] =
    useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (Object.keys(eventSponsorData).length > 0) {
      setDataSource([
        {
          id: eventSponsorData.id,
          attendee_first_name: eventSponsorData.attendee_first_name,
          attendee_last_name: eventSponsorData.attendee_last_name,
          attendee_email: eventSponsorData.attendee_email,
          attendee_company: eventSponsorData.attendee_company,
        },
      ]);
    } else {
      if (sponsorCreatedSuccessFully) {
        form.setFieldsValue({
          attendee_email_confirm: '',
          attendee_first_name: '',
          attendee_last_name: '',
          attendee_email: '',
          attendee_company: '',
        });
      }
      setEditingKey('1');
      setDataSource([
        {
          id: '1',
          attendee_first_name: '',
          attendee_last_name: '',
          attendee_email: '',
          attendee_company: '',
        },
      ]);
    }
  }, [eventSponsorData, localUserData]);

  useEffect(() => {
    if (eventSponsorData?.length === 0) {
      const loading = !eventSponsorData || eventSponsorData.length === 0;
      setLoading(loading);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [eventSponsorData]);

  const handleSave = async (key) => {
    let row;
    try {
      row = await form.validateFields();
    } catch (errInfo) {
      scrollAndFocusFormErrorField(errInfo, form);
      return;
    }
    const sponsorData = {
      ...row,
      id: key.id,
      registration_type: 'In-Person',
    };
    onFinish(sponsorData);
    setEditingKey('');
  };

  const edit = (record) => {
    form.setFieldsValue({
      attendee_email_confirm: record?.attendee_email
        ? record?.attendee_email
        : '',
      attendee_first_name: record.attendee_first_name,
      attendee_last_name: record.attendee_last_name,
      attendee_email: record.attendee_email,
      attendee_company: record.attendee_company,
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const columns = [
    {
      title: ApplicationString['sponsorDetailForm.contactPersonFirstName'],
      dataIndex: 'attendee_first_name',
      width: '6%',
      editable: true,
      render: (text) => {
        return <Tag prefixCls='regularAttendeesTag'>{text}</Tag>;
      },
    },
    {
      title: ApplicationString['sponsorDetailForm.contactPersonLastName'],
      dataIndex: 'attendee_last_name',
      width: '6%',
      editable: true,
      render: (text) => {
        return <Tag prefixCls='regularAttendeesTag'>{text}</Tag>;
      },
    },
    {
      title: ApplicationString['sponsorDetailForm.contactPersonEmail'],
      dataIndex: 'attendee_email',
      width: '8%',
      editable: true,
      render: (text) => {
        return <Tag prefixCls='regularAttendeesTag'>{text}</Tag>;
      },
    },
    {
      title: ApplicationString['sponsorDetailForm.companyAffiliation'],
      dataIndex: 'attendee_company',
      width: '6%',
      editable: true,
      render: (text) => {
        return <Tag prefixCls='regularAttendeesTag'>{text}</Tag>;
      },
    },
    {
      title: ' ',
      dataIndex: 'action',
      width: '4%',
      render: (_, record) => {
        const hasEventSponsors =
          Object.keys(eventSponsorData).length > 0 ? true : false;
        return (
          <AdminActionsDropdown
            record={record}
            usedForAttendeeTable={true}
            editBool={onlyForView ? false : true}
            attendeeEditingKey={editingKey}
            handleCancelClick={cancel}
            handleSaveClick={handleSave}
            deleteBool={false}
            handleEditClick={edit}
            cancelBool={hasEventSponsors}
            ToolTipTitle={'Sponsor'}
          />
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const renderBenefits = (benefits) => {
    return (
      <div
        className='perks-inter'
        dangerouslySetInnerHTML={{__html: benefits}}
      />
    );
  };

  return (
    <Form
      form={form}
      onValuesChange={(changedValues) =>
        formValueChangeHandler(form, changedValues)
      }>
      <Skeleton
        loading={loading}
        prefixCls='table-skeleton'
        active
        paragraph={{rows: eventSponsorData?.length}}>
        <div className='attendees-content-container'>
          <div className='attendees-label'>
            {ApplicationString['userAddAttendeeComponent.sponsorDetail.label']}
          </div>
          {alertInformation && alertInformation !== '<p></p>' && (
            <div className='attendees-description-container'>
              <Alert
                prefixCls='attendees-description-alert'
                message={renderBenefits(alertInformation)}
                type='info'
                showIcon
              />
            </div>
          )}
        </div>

        <Table
          prefixCls='attendees-table-container'
          components={{
            body: {
              cell: (props) => (
                <EditableCell {...props} usedForSponsorDetails />
              ),
            },
          }}
          scroll={{x: columns?.length * 150}}
          rowClassName={() => 'editable-row'}
          dataSource={dataSource}
          columns={mergedColumns}
          pagination={false}
        />
      </Skeleton>
    </Form>
  );
};

export default SponsorDetailCartForm;
